import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { Palette, X as XIcon, CheckCircle, Loader2, Sparkles, RefreshCw } from 'lucide-react';
import { studentAPI } from '@/lib/api';

const C = { card: '#1A2236', cream: '#F8F5EE', muted: '#94A3B8', gold: '#D4A853' };

// ============================================================================
// Scene drawing — each function paints a recognizable illustrated scene onto a
// canvas context. Output is cached per (id, width, height) so the heavy work
// only happens once per resize, not every animation frame.
// ============================================================================

// Seeded random so scattered elements (stars, leaves) stay still between frames.
function makeRand(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function drawBeach(ctx, w, h) {
  // Sky
  let g = ctx.createLinearGradient(0, 0, 0, h * 0.4);
  g.addColorStop(0, '#FFD89B');
  g.addColorStop(1, '#7CC1E8');
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h * 0.4);
  // Ocean
  g = ctx.createLinearGradient(0, h * 0.4, 0, h * 0.7);
  g.addColorStop(0, '#3B82F6');
  g.addColorStop(1, '#1E40AF');
  ctx.fillStyle = g; ctx.fillRect(0, h * 0.4, w, h * 0.3);
  // Sand
  g = ctx.createLinearGradient(0, h * 0.7, 0, h);
  g.addColorStop(0, '#F4D6A3');
  g.addColorStop(1, '#E0B27D');
  ctx.fillStyle = g; ctx.fillRect(0, h * 0.7, w, h * 0.3);
  // Sun
  ctx.fillStyle = '#FFE066';
  ctx.beginPath(); ctx.arc(w * 0.8, h * 0.15, Math.min(w, h) * 0.07, 0, Math.PI * 2); ctx.fill();
  // Wave lines
  ctx.strokeStyle = 'rgba(255,255,255,0.4)';
  ctx.lineWidth = Math.max(1, h * 0.003);
  for (let i = 0; i < 6; i++) {
    const y = h * 0.45 + i * (h * 0.04);
    ctx.beginPath();
    for (let x = 0; x <= w; x += 8) {
      const yy = y + Math.sin((x / w) * Math.PI * 4 + i) * 3;
      if (x === 0) ctx.moveTo(x, yy); else ctx.lineTo(x, yy);
    }
    ctx.stroke();
  }
  // Palm tree silhouette (right)
  ctx.fillStyle = '#3F2A1A';
  ctx.fillRect(w * 0.12 - w * 0.008, h * 0.45, w * 0.016, h * 0.4);
  ctx.fillStyle = '#2F5E2F';
  for (let a = -Math.PI / 2; a <= Math.PI / 2; a += Math.PI / 6) {
    ctx.save();
    ctx.translate(w * 0.12, h * 0.45);
    ctx.rotate(a);
    ctx.beginPath();
    ctx.ellipse(0, -h * 0.04, w * 0.045, h * 0.012, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function drawParkAndCourt(ctx, w, h) {
  // Sky
  let g = ctx.createLinearGradient(0, 0, 0, h * 0.5);
  g.addColorStop(0, '#87CEEB');
  g.addColorStop(1, '#C6E4F2');
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h * 0.5);
  // Distant hills
  ctx.fillStyle = '#7BBE6A';
  ctx.beginPath();
  ctx.moveTo(0, h * 0.55);
  ctx.quadraticCurveTo(w * 0.25, h * 0.42, w * 0.5, h * 0.5);
  ctx.quadraticCurveTo(w * 0.75, h * 0.58, w, h * 0.48);
  ctx.lineTo(w, h * 0.55);
  ctx.closePath();
  ctx.fill();
  // Grass
  g = ctx.createLinearGradient(0, h * 0.5, 0, h);
  g.addColorStop(0, '#5A9F4A');
  g.addColorStop(1, '#3F7A33');
  ctx.fillStyle = g; ctx.fillRect(0, h * 0.5, w, h * 0.5);
  // Trees
  const drawTree = (cx, by, scale) => {
    ctx.fillStyle = '#3F2A1A';
    ctx.fillRect(cx - 3 * scale, by - 40 * scale, 6 * scale, 40 * scale);
    ctx.fillStyle = '#2E6F2A';
    ctx.beginPath();
    ctx.arc(cx, by - 50 * scale, 22 * scale, 0, Math.PI * 2);
    ctx.fill();
  };
  const treeScale = Math.min(w, h) / 220;
  drawTree(w * 0.08, h * 0.6, treeScale);
  drawTree(w * 0.18, h * 0.58, treeScale * 0.85);
  drawTree(w * 0.92, h * 0.6, treeScale);
  // Basketball court
  const cx = w * 0.5, cy = h * 0.85, cw = w * 0.55, ch = h * 0.22;
  ctx.fillStyle = '#D97447'; // orange court
  ctx.beginPath();
  ctx.moveTo(cx - cw / 2, cy + ch / 2);
  ctx.lineTo(cx + cw / 2, cy + ch / 2);
  ctx.lineTo(cx + cw / 3, cy - ch / 2);
  ctx.lineTo(cx - cw / 3, cy - ch / 2);
  ctx.closePath();
  ctx.fill();
  // Court lines
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = Math.max(1.5, h * 0.004);
  ctx.beginPath(); ctx.moveTo(cx, cy + ch / 2); ctx.lineTo(cx, cy - ch / 2); ctx.stroke();
  ctx.beginPath(); ctx.ellipse(cx, cy, w * 0.06, h * 0.025, 0, 0, Math.PI * 2); ctx.stroke();
  // Hoop pole
  ctx.fillStyle = '#444';
  ctx.fillRect(cx - 2, cy - ch / 2 - h * 0.18, 3, h * 0.18);
  ctx.strokeStyle = '#EF4444';
  ctx.lineWidth = Math.max(2, h * 0.006);
  ctx.beginPath();
  ctx.moveTo(cx - w * 0.04, cy - ch / 2 - h * 0.13);
  ctx.lineTo(cx + w * 0.04, cy - ch / 2 - h * 0.13);
  ctx.stroke();
}

function drawLibrary(ctx, w, h) {
  // Wood wall background
  ctx.fillStyle = '#5A3A22';
  ctx.fillRect(0, 0, w, h);
  // Bookshelves: 4 rows
  const rows = 4;
  const rowH = h / rows;
  const colors = ['#B91C1C', '#1E3A8A', '#854D0E', '#15803D', '#7C2D12', '#0E7490', '#A16207', '#831843'];
  const rand = makeRand(42);
  for (let r = 0; r < rows; r++) {
    const yTop = r * rowH + 4;
    const yBot = (r + 1) * rowH - 6;
    // Shelf board
    ctx.fillStyle = '#3F2A1A';
    ctx.fillRect(0, yBot, w, 6);
    // Books
    let x = 0;
    while (x < w) {
      const bw = 14 + rand() * 18;
      const lean = (rand() - 0.5) * 4;
      const color = colors[Math.floor(rand() * colors.length)];
      ctx.save();
      ctx.translate(x + bw / 2, yBot);
      ctx.rotate((lean * Math.PI) / 180);
      ctx.fillStyle = color;
      ctx.fillRect(-bw / 2, -(yBot - yTop), bw - 2, yBot - yTop);
      // Title line
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.fillRect(-bw / 2 + 2, -(yBot - yTop) * 0.6, bw - 6, 2);
      ctx.restore();
      x += bw;
    }
  }
  // Reading-lamp warmth in corner
  const lamp = ctx.createRadialGradient(w * 0.92, h * 0.18, 5, w * 0.92, h * 0.18, w * 0.25);
  lamp.addColorStop(0, 'rgba(255,220,140,0.5)');
  lamp.addColorStop(1, 'rgba(255,220,140,0)');
  ctx.fillStyle = lamp;
  ctx.fillRect(0, 0, w, h);
}

function drawClassroom(ctx, w, h) {
  // Wall
  ctx.fillStyle = '#F5E6C8';
  ctx.fillRect(0, 0, w, h * 0.78);
  // Floor
  ctx.fillStyle = '#A98763';
  ctx.fillRect(0, h * 0.78, w, h * 0.22);
  // Chalkboard
  const bx = w * 0.15, by = h * 0.12, bw = w * 0.7, bh = h * 0.5;
  ctx.fillStyle = '#3F2A1A'; // frame
  ctx.fillRect(bx - 10, by - 10, bw + 20, bh + 20);
  ctx.fillStyle = '#1F4A3A';
  ctx.fillRect(bx, by, bw, bh);
  // Chalk lines (math-y squiggles)
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 2;
  for (let i = 0; i < 4; i++) {
    const y = by + 25 + i * 30;
    ctx.beginPath(); ctx.moveTo(bx + 30, y); ctx.lineTo(bx + bw * (0.3 + i * 0.15), y); ctx.stroke();
  }
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `bold ${Math.floor(h * 0.05)}px sans-serif`;
  ctx.fillText('A B C', bx + 30, by + bh - 30);
  // Desks (silhouettes)
  ctx.fillStyle = '#8B5E3C';
  for (let i = 0; i < 4; i++) {
    const dx = w * (0.08 + i * 0.22);
    ctx.fillRect(dx, h * 0.8, w * 0.14, h * 0.06);
    ctx.fillRect(dx + 4, h * 0.86, 4, h * 0.1);
    ctx.fillRect(dx + w * 0.14 - 8, h * 0.86, 4, h * 0.1);
  }
}

function drawMountains(ctx, w, h) {
  // Sky
  let g = ctx.createLinearGradient(0, 0, 0, h * 0.7);
  g.addColorStop(0, '#5FA8E8');
  g.addColorStop(1, '#C4E4F8');
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h * 0.7);
  // Sun
  ctx.fillStyle = '#FFF9C4';
  ctx.beginPath(); ctx.arc(w * 0.78, h * 0.18, Math.min(w, h) * 0.06, 0, Math.PI * 2); ctx.fill();
  // Far mountains
  ctx.fillStyle = '#6B7A8F';
  ctx.beginPath();
  ctx.moveTo(0, h * 0.55);
  ctx.lineTo(w * 0.15, h * 0.35);
  ctx.lineTo(w * 0.3, h * 0.5);
  ctx.lineTo(w * 0.5, h * 0.32);
  ctx.lineTo(w * 0.7, h * 0.48);
  ctx.lineTo(w * 0.85, h * 0.38);
  ctx.lineTo(w, h * 0.5);
  ctx.lineTo(w, h * 0.6);
  ctx.lineTo(0, h * 0.6);
  ctx.closePath();
  ctx.fill();
  // Snow caps on far mountains
  ctx.fillStyle = '#FFFFFF';
  const caps = [
    [w * 0.15, h * 0.35],
    [w * 0.5, h * 0.32],
    [w * 0.85, h * 0.38],
  ];
  caps.forEach(([px, py]) => {
    ctx.beginPath();
    ctx.moveTo(px - 18, py + 22);
    ctx.lineTo(px, py);
    ctx.lineTo(px + 18, py + 22);
    ctx.closePath();
    ctx.fill();
  });
  // Near mountains
  ctx.fillStyle = '#3E5C4A';
  ctx.beginPath();
  ctx.moveTo(0, h * 0.7);
  ctx.lineTo(w * 0.25, h * 0.5);
  ctx.lineTo(w * 0.45, h * 0.65);
  ctx.lineTo(w * 0.7, h * 0.48);
  ctx.lineTo(w, h * 0.62);
  ctx.lineTo(w, h * 0.75);
  ctx.lineTo(0, h * 0.75);
  ctx.closePath();
  ctx.fill();
  // Ground
  ctx.fillStyle = '#2D4A2F';
  ctx.fillRect(0, h * 0.75, w, h * 0.25);
}

function drawSunset(ctx, w, h) {
  // Sky gradient
  let g = ctx.createLinearGradient(0, 0, 0, h * 0.65);
  g.addColorStop(0, '#1B1B6B');
  g.addColorStop(0.5, '#E94B3C');
  g.addColorStop(1, '#FFB366');
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h * 0.65);
  // Sun
  const sx = w * 0.5, sy = h * 0.55, sr = Math.min(w, h) * 0.1;
  const sg = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr * 1.5);
  sg.addColorStop(0, '#FFEBA3');
  sg.addColorStop(0.5, '#FFC368');
  sg.addColorStop(1, 'rgba(255,150,80,0)');
  ctx.fillStyle = sg;
  ctx.beginPath(); ctx.arc(sx, sy, sr * 1.5, 0, Math.PI * 2); ctx.fill();
  // Water
  g = ctx.createLinearGradient(0, h * 0.65, 0, h);
  g.addColorStop(0, '#5A3A6E');
  g.addColorStop(1, '#1B1B4B');
  ctx.fillStyle = g; ctx.fillRect(0, h * 0.65, w, h * 0.35);
  // Sun reflection on water
  ctx.fillStyle = 'rgba(255,200,120,0.55)';
  ctx.fillRect(sx - sr * 0.4, h * 0.65, sr * 0.8, h * 0.3);
  // Birds (V shapes)
  ctx.strokeStyle = '#1B1B4B';
  ctx.lineWidth = Math.max(1.5, h * 0.003);
  const drawBird = (bx, by, scale) => {
    ctx.beginPath();
    ctx.moveTo(bx - 8 * scale, by);
    ctx.lineTo(bx, by - 4 * scale);
    ctx.lineTo(bx + 8 * scale, by);
    ctx.stroke();
  };
  drawBird(w * 0.18, h * 0.2, 1);
  drawBird(w * 0.28, h * 0.15, 0.8);
  drawBird(w * 0.78, h * 0.22, 1.1);
  drawBird(w * 0.85, h * 0.18, 0.9);
}

function drawSpace(ctx, w, h) {
  // Background
  const bg = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h));
  bg.addColorStop(0, '#1B0B3F');
  bg.addColorStop(1, '#02020A');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);
  // Nebula puffs
  const neb = ctx.createRadialGradient(w * 0.3, h * 0.3, 0, w * 0.3, h * 0.3, w * 0.4);
  neb.addColorStop(0, 'rgba(168,85,247,0.35)');
  neb.addColorStop(1, 'rgba(168,85,247,0)');
  ctx.fillStyle = neb; ctx.fillRect(0, 0, w, h);
  const neb2 = ctx.createRadialGradient(w * 0.8, h * 0.7, 0, w * 0.8, h * 0.7, w * 0.4);
  neb2.addColorStop(0, 'rgba(56,189,248,0.25)');
  neb2.addColorStop(1, 'rgba(56,189,248,0)');
  ctx.fillStyle = neb2; ctx.fillRect(0, 0, w, h);
  // Stars
  const rand = makeRand(7);
  ctx.fillStyle = '#FFFFFF';
  for (let i = 0; i < 220; i++) {
    const x = rand() * w;
    const y = rand() * h;
    const r = rand() * 1.5 + 0.3;
    ctx.globalAlpha = 0.4 + rand() * 0.6;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;
  // Planet (Saturn-like)
  const px = w * 0.75, py = h * 0.3, pr = Math.min(w, h) * 0.09;
  const planet = ctx.createRadialGradient(px - pr * 0.3, py - pr * 0.3, 0, px, py, pr);
  planet.addColorStop(0, '#FBBF24');
  planet.addColorStop(1, '#92400E');
  ctx.fillStyle = planet;
  ctx.beginPath(); ctx.arc(px, py, pr, 0, Math.PI * 2); ctx.fill();
  // Ring
  ctx.strokeStyle = 'rgba(251,191,36,0.8)';
  ctx.lineWidth = Math.max(2, h * 0.006);
  ctx.beginPath();
  ctx.ellipse(px, py, pr * 1.7, pr * 0.4, -0.4, 0, Math.PI * 2);
  ctx.stroke();
  // Small moon
  ctx.fillStyle = '#E5E7EB';
  ctx.beginPath(); ctx.arc(w * 0.18, h * 0.62, pr * 0.4, 0, Math.PI * 2); ctx.fill();
}

function drawForest(ctx, w, h) {
  // Sky peek
  let g = ctx.createLinearGradient(0, 0, 0, h * 0.4);
  g.addColorStop(0, '#A8D5E8');
  g.addColorStop(1, '#7FB069');
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
  // Ground
  g = ctx.createLinearGradient(0, h * 0.7, 0, h);
  g.addColorStop(0, '#3E5C2A');
  g.addColorStop(1, '#1F3A1F');
  ctx.fillStyle = g; ctx.fillRect(0, h * 0.7, w, h * 0.3);
  // Tree trunks (vertical)
  const trunks = [
    { x: 0.08, scale: 1.1 },
    { x: 0.22, scale: 0.9 },
    { x: 0.35, scale: 1.05 },
    { x: 0.5,  scale: 0.95 },
    { x: 0.62, scale: 1.0 },
    { x: 0.78, scale: 1.15 },
    { x: 0.9,  scale: 0.9 },
  ];
  trunks.forEach(({ x, scale }) => {
    const tx = w * x;
    const tw = w * 0.025 * scale;
    ctx.fillStyle = '#3F2A1A';
    ctx.fillRect(tx - tw / 2, 0, tw, h);
  });
  // Canopy mass
  ctx.fillStyle = 'rgba(46,111,42,0.92)';
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(w, 0);
  ctx.lineTo(w, h * 0.45);
  ctx.quadraticCurveTo(w * 0.75, h * 0.25, w * 0.5, h * 0.4);
  ctx.quadraticCurveTo(w * 0.25, h * 0.55, 0, h * 0.35);
  ctx.closePath();
  ctx.fill();
  // Sunbeam shafts
  ctx.fillStyle = 'rgba(255,236,150,0.18)';
  for (let i = 0; i < 3; i++) {
    const sx = w * (0.25 + i * 0.25);
    ctx.beginPath();
    ctx.moveTo(sx, 0);
    ctx.lineTo(sx + 20, 0);
    ctx.lineTo(sx + 80, h * 0.7);
    ctx.lineTo(sx + 40, h * 0.7);
    ctx.closePath();
    ctx.fill();
  }
}

// Background catalog. `kind: 'none'` = raw camera passthrough (no segmentation).
// `kind: 'blur'` = blur the original feed. `kind: 'scene'` = paint the provided
// draw function to the page-sized canvas behind the segmented person.
const VIDEO_BACKGROUNDS = [
  { id: 'none', name: 'No Background', kind: 'none',
    swatch: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)' },
  { id: 'blur', name: 'Blur', kind: 'blur',
    swatch: 'linear-gradient(135deg, rgba(99,102,241,0.4), rgba(56,189,248,0.4))' },
  { id: 'beach', name: 'Beach', kind: 'scene', draw: drawBeach,
    swatch: 'linear-gradient(180deg, #FFD89B 0%, #3B82F6 45%, #F4D6A3 75%)' },
  { id: 'park-court', name: 'Park & Court', kind: 'scene', draw: drawParkAndCourt,
    swatch: 'linear-gradient(180deg, #87CEEB 0%, #5A9F4A 60%, #D97447 80%)' },
  { id: 'library', name: 'Library', kind: 'scene', draw: drawLibrary,
    swatch: 'linear-gradient(135deg, #B91C1C 0%, #1E3A8A 33%, #854D0E 66%, #15803D 100%)' },
  { id: 'classroom', name: 'Classroom', kind: 'scene', draw: drawClassroom,
    swatch: 'linear-gradient(135deg, #F5E6C8 0%, #1F4A3A 40%, #A98763 100%)' },
  { id: 'mountains', name: 'Mountains', kind: 'scene', draw: drawMountains,
    swatch: 'linear-gradient(180deg, #5FA8E8 0%, #6B7A8F 50%, #2D4A2F 100%)' },
  { id: 'sunset', name: 'Sunset Ocean', kind: 'scene', draw: drawSunset,
    swatch: 'linear-gradient(180deg, #1B1B6B 0%, #E94B3C 40%, #FFB366 65%, #1B1B4B 100%)' },
  { id: 'space', name: 'Outer Space', kind: 'scene', draw: drawSpace,
    swatch: 'radial-gradient(ellipse at 30% 30%, rgba(168,85,247,0.6) 0%, transparent 50%), radial-gradient(ellipse at 75% 30%, #FBBF24 0%, #92400E 5%, transparent 12%), #0F0F23' },
  { id: 'forest', name: 'Forest', kind: 'scene', draw: drawForest,
    swatch: 'linear-gradient(180deg, #A8D5E8 0%, #2E6F2A 40%, #1F3A1F 100%)' },
];

function makeStarField(w, h) {
  // legacy helper kept in case the older background id 'stars' lingers in localStorage
  const off = document.createElement('canvas');
  off.width = w; off.height = h;
  drawSpace(off.getContext('2d'), w, h);
  return off;
}

// ============================================================================
// Component
// ============================================================================
const VirtualBackgroundCamera = forwardRef(({ studentId, onError }, ref) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const segmenterRef = useRef(null);
  const rafRef = useRef(null);
  const sceneCacheRef = useRef({});           // id -> cached offscreen canvas
  const lastSegmentTsRef = useRef(0);
  const lastMaskCanvasRef = useRef(null);
  const personValueRef = useRef(null);         // discovered on first frame: 0 or non-zero means person

  const [background, setBackground] = useState(() => {
    try {
      const stored = localStorage.getItem(`sv_video_bg_${studentId || 'anon'}`);
      const found = VIDEO_BACKGROUNDS.find((b) => b.id === stored);
      if (found) return found;
    } catch { /* noop */ }
    return VIDEO_BACKGROUNDS[0];
  });
  const [showPicker, setShowPicker] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [segmenterReady, setSegmenterReady] = useState(false);
  const [segmenterError, setSegmenterError] = useState(null);

  // Personal "Wishful Places" — generated by the backend from the child's
  // profile interests. Each entry: { id, name, prompt, image_url, generated_date }.
  const [personalBackgrounds, setPersonalBackgrounds] = useState([]);
  const [personalLoading, setPersonalLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const personalImagesRef = useRef({});           // id -> { state, img }
  const [imageBump, setImageBump] = useState(0);   // force-rerender when images load

  // Adapt server entries into background catalog entries (kind: 'image').
  const personalAsBackgrounds = React.useMemo(
    () =>
      (personalBackgrounds || []).map((p) => ({
        id: p.id,
        name: p.name,
        kind: 'image',
        image_url: p.image_url,
        prompt: p.prompt,
        swatch: `url("${p.image_url}") center/cover, #1F2937`,
      })),
    [personalBackgrounds],
  );

  const preloadPersonalImages = React.useCallback((items) => {
    items.forEach((bg) => {
      if (personalImagesRef.current[bg.id]?.state === 'loaded') return;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      personalImagesRef.current[bg.id] = { state: 'loading', img };
      img.onload = () => {
        personalImagesRef.current[bg.id] = { state: 'loaded', img };
        setImageBump((n) => n + 1);
      };
      img.onerror = () => {
        personalImagesRef.current[bg.id] = { state: 'error', img: null };
        setImageBump((n) => n + 1);
      };
      img.src = bg.image_url;
    });
  }, []);

  // Fetch personal backgrounds on mount.
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setPersonalLoading(true);
    studentAPI.getVirtualBackgrounds(studentId)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.backgrounds || [];
        setPersonalBackgrounds(list);
        preloadPersonalImages(list);
      })
      .catch(() => { /* soft fail — picker still shows canned scenes */ })
      .finally(() => { if (!cancelled) setPersonalLoading(false); });
    return () => { cancelled = true; };
  }, [studentId, preloadPersonalImages]);

  const handleRegenerate = React.useCallback(async () => {
    if (!studentId || regenerating) return;
    setRegenerating(true);
    try {
      const res = await studentAPI.regenerateVirtualBackgrounds(studentId);
      const list = res?.data?.backgrounds || [];
      personalImagesRef.current = {};   // drop old caches
      setPersonalBackgrounds(list);
      preloadPersonalImages(list);
    } catch (err) {
      // surface failure but don't kill the recorder
      console.warn('Regenerate backgrounds failed:', err);
    } finally {
      setRegenerating(false);
    }
  }, [studentId, regenerating, preloadPersonalImages]);

  useEffect(() => {
    try { localStorage.setItem(`sv_video_bg_${studentId || 'anon'}`, background.id); } catch { /* noop */ }
  }, [background, studentId]);

  useImperativeHandle(ref, () => ({
    getCompositeStream(fps = 30) {
      const canvas = canvasRef.current;
      const camStream = cameraStreamRef.current;
      if (!canvas || !camStream) return null;
      const canvasStream = canvas.captureStream(fps);
      const videoTrack = canvasStream.getVideoTracks()[0];
      const audioTrack = camStream.getAudioTracks()[0];
      const tracks = [videoTrack];
      if (audioTrack) tracks.push(audioTrack);
      return new MediaStream(tracks);
    },
    stop() {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (cameraStreamRef.current) cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      if (segmenterRef.current?.close) {
        try { segmenterRef.current.close(); } catch { /* noop */ }
      }
      cameraStreamRef.current = null;
      segmenterRef.current = null;
    },
  }), []);

  // Camera + segmenter init.
  useEffect(() => {
    let cancelled = false;

    async function setupCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
          audio: true,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        cameraStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCameraReady(true);
      } catch (err) {
        if (!cancelled) {
          setSegmenterError(err.message || 'Camera access denied');
          if (onError) onError(err);
        }
      }
    }

    async function setupSegmenter() {
      try {
        const mod = await import(
          /* webpackIgnore: true */
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/vision_bundle.mjs'
        );
        const { ImageSegmenter, FilesetResolver } = mod;
        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm'
        );
        const seg = await ImageSegmenter.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          outputCategoryMask: true,
          outputConfidenceMasks: false,
        });
        if (cancelled) { try { seg.close(); } catch { /* noop */ } return; }
        segmenterRef.current = seg;
        setSegmenterReady(true);
      } catch (err) {
        console.warn('Selfie segmenter failed to load:', err);
        setSegmenterError(err.message || 'Background effects unavailable');
      }
    }

    setupCamera();
    setupSegmenter();

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (cameraStreamRef.current) cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      if (segmenterRef.current?.close) {
        try { segmenterRef.current.close(); } catch { /* noop */ }
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cache or rebuild scene art for the current canvas size.
  function getSceneCanvas(bg, w, h) {
    const cache = sceneCacheRef.current[bg.id];
    if (cache && cache.width === w && cache.height === h) return cache;
    const off = document.createElement('canvas');
    off.width = w; off.height = h;
    const offCtx = off.getContext('2d');
    bg.draw(offCtx, w, h);
    sceneCacheRef.current[bg.id] = off;
    return off;
  }

  // Render loop.
  useEffect(() => {
    if (!cameraReady) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });

    const personCanvas = document.createElement('canvas');
    const personCtx = personCanvas.getContext('2d');
    const maskCanvas = document.createElement('canvas');
    const maskCtx = maskCanvas.getContext('2d');

    const render = () => {
      const w = video.videoWidth || 640;
      const h = video.videoHeight || 480;
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;

      if (video.readyState >= 2) {
        if (background.kind === 'none' || !segmenterRef.current) {
          ctx.drawImage(video, 0, 0, w, h);
        } else {
          // Run segmentation, throttled to ~25fps to leave headroom.
          let mask = null;
          const now = performance.now();
          if (now - lastSegmentTsRef.current > 40) {
            try {
              const result = segmenterRef.current.segmentForVideo(video, now);
              if (result?.categoryMask) {
                mask = result.categoryMask;
                lastSegmentTsRef.current = now;
              }
            } catch (e) { /* skip frame */ }
          }

          if (mask) {
            const mw = mask.width;
            const mh = mask.height;
            const data = mask.getAsUint8Array();

            // First-frame auto-detect: count zero pixels. In a real webcam frame
            // the person occupies ~20–60% of pixels, so the LARGER class is the
            // background. We let that observation pick which value represents
            // the person — this neutralizes any 0/1 inversion across MediaPipe
            // model variants so the user never sees an inverted silhouette.
            if (personValueRef.current === null) {
              let zeros = 0;
              for (let i = 0; i < data.length; i++) if (data[i] === 0) zeros++;
              const zeroRatio = zeros / data.length;
              personValueRef.current = zeroRatio < 0.5 ? 0 : 1;
            }
            const personValue = personValueRef.current;

            if (maskCanvas.width !== mw) maskCanvas.width = mw;
            if (maskCanvas.height !== mh) maskCanvas.height = mh;
            const imageData = maskCtx.createImageData(mw, mh);
            for (let i = 0; i < data.length; i++) {
              const isPerson = personValue === 0 ? data[i] === 0 : data[i] !== 0;
              imageData.data[i * 4 + 0] = 255;
              imageData.data[i * 4 + 1] = 255;
              imageData.data[i * 4 + 2] = 255;
              imageData.data[i * 4 + 3] = isPerson ? 255 : 0;
            }
            maskCtx.putImageData(imageData, 0, 0);
            try { mask.close && mask.close(); } catch { /* noop */ }
            lastMaskCanvasRef.current = maskCanvas;
          }

          // Step 1: paint the chosen background.
          if (background.kind === 'blur') {
            ctx.save();
            ctx.filter = 'blur(15px)';
            ctx.drawImage(video, 0, 0, w, h);
            ctx.filter = 'none';
            ctx.restore();
          } else if (background.kind === 'scene') {
            ctx.drawImage(getSceneCanvas(background, w, h), 0, 0);
          } else if (background.kind === 'image') {
            const entry = personalImagesRef.current[background.id];
            if (entry?.state === 'loaded' && entry.img?.complete && entry.img.naturalWidth > 0) {
              // cover-fit: scale and center-crop to fill the canvas.
              const iw = entry.img.naturalWidth;
              const ih = entry.img.naturalHeight;
              const scale = Math.max(w / iw, h / ih);
              const dw = iw * scale;
              const dh = ih * scale;
              ctx.drawImage(entry.img, (w - dw) / 2, (h - dh) / 2, dw, dh);
            } else {
              // Image still loading — soft placeholder so the student doesn't see noise.
              const g = ctx.createLinearGradient(0, 0, 0, h);
              g.addColorStop(0, '#1F2937');
              g.addColorStop(1, '#0F172A');
              ctx.fillStyle = g;
              ctx.fillRect(0, 0, w, h);
            }
          } else {
            // Fallback for legacy/unknown kinds — black fill.
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, w, h);
          }

          // Step 2: extract person from camera using mask and composite on top.
          if (lastMaskCanvasRef.current) {
            if (personCanvas.width !== w) personCanvas.width = w;
            if (personCanvas.height !== h) personCanvas.height = h;
            personCtx.globalCompositeOperation = 'source-over';
            personCtx.clearRect(0, 0, w, h);
            personCtx.drawImage(video, 0, 0, w, h);
            personCtx.globalCompositeOperation = 'destination-in';
            personCtx.drawImage(lastMaskCanvasRef.current, 0, 0, w, h);
            personCtx.globalCompositeOperation = 'source-over';
            ctx.drawImage(personCanvas, 0, 0, w, h);
          } else {
            ctx.drawImage(video, 0, 0, w, h);
          }
        }
      }
      rafRef.current = requestAnimationFrame(render);
    };

    render();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [cameraReady, background]);

  const segmenterUnavailable = !!segmenterError && !cameraStreamRef.current;
  const visibleBackgrounds = segmenterError
    ? VIDEO_BACKGROUNDS.filter((b) => b.id === 'none')
    : VIDEO_BACKGROUNDS;
  const visiblePersonal = segmenterError ? [] : personalAsBackgrounds;
  // Touch imageBump so the picker re-renders when previews finish loading.
  void imageBump;

  return (
    <div className="relative rounded-xl overflow-hidden" style={{ background: '#000' }}>
      <video ref={videoRef} playsInline muted style={{ display: 'none' }} />
      <canvas
        ref={canvasRef}
        className="w-full h-auto block"
        style={{ maxHeight: '320px', objectFit: 'contain' }}
        data-testid="virtual-bg-canvas"
      />

      {!cameraReady && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="flex items-center gap-2" style={{ color: C.cream }}>
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm font-bold">Starting camera...</span>
          </div>
        </div>
      )}

      {cameraReady && (
        <div className="absolute bottom-2 left-2">
          <button
            onClick={() => setShowPicker((v) => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-105"
            style={{ background: 'rgba(0,0,0,0.6)', color: C.cream, border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)' }}
            data-testid="virtual-bg-picker-btn"
            title="Change background"
          >
            <Palette size={14} style={{ color: C.gold }} />
            <span>{background.name}</span>
          </button>

          {showPicker && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowPicker(false)} />
              <div
                className="absolute bottom-full mb-2 left-0 z-50 p-3 rounded-xl shadow-2xl"
                style={{ background: C.card, border: `1px solid ${C.gold}`, minWidth: '320px', maxHeight: '70vh', overflowY: 'auto' }}
                data-testid="virtual-bg-picker-popover"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase" style={{ color: C.muted }}>Video Background</span>
                  <button onClick={() => setShowPicker(false)} style={{ color: C.muted }} aria-label="Close">
                    <XIcon size={14} />
                  </button>
                </div>

                {visiblePersonal.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-1.5 mt-1">
                      <span className="flex items-center gap-1 text-[10px] font-bold uppercase" style={{ color: C.gold }}>
                        <Sparkles size={11} /> My Wishful Places
                      </span>
                      <button
                        onClick={handleRegenerate}
                        disabled={regenerating}
                        className="flex items-center gap-1 text-[10px] font-bold transition-all hover:scale-105 disabled:opacity-50"
                        style={{ color: C.muted }}
                        title="Generate a new set based on your interests"
                        data-testid="virtual-bg-regenerate-btn"
                      >
                        {regenerating
                          ? <Loader2 size={10} className="animate-spin" />
                          : <RefreshCw size={10} />}
                        New set
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {visiblePersonal.map((bg) => {
                        const isSelected = bg.id === background.id;
                        const entry = personalImagesRef.current[bg.id];
                        const isLoading = !entry || entry.state === 'loading';
                        const errored = entry?.state === 'error';
                        return (
                          <button
                            key={bg.id}
                            onClick={() => { setBackground(bg); setShowPicker(false); }}
                            className="flex items-center gap-2 p-1.5 rounded-lg text-left transition-all hover:scale-[1.02]"
                            style={{
                              background: isSelected ? 'rgba(212,168,83,0.15)' : 'rgba(255,255,255,0.03)',
                              border: `1px solid ${isSelected ? C.gold : 'rgba(255,255,255,0.08)'}`,
                            }}
                            data-testid={`virtual-bg-option-${bg.id}`}
                          >
                            <span
                              className="relative block w-10 h-10 rounded-md flex-shrink-0 overflow-hidden"
                              style={{ background: bg.swatch, border: '1px solid rgba(0,0,0,0.3)' }}
                            >
                              {isLoading && (
                                <span className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
                                  <Loader2 size={12} className="animate-spin" style={{ color: C.gold }} />
                                </span>
                              )}
                            </span>
                            <span className="text-xs font-semibold truncate" style={{ color: errored ? C.muted : C.cream }}>{bg.name}</span>
                            {isSelected && <CheckCircle size={12} style={{ color: C.gold, marginLeft: 'auto' }} />}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {personalLoading && visiblePersonal.length === 0 && (
                  <div className="mb-3 flex items-center gap-1.5 text-[10px]" style={{ color: C.muted }}>
                    <Loader2 size={11} className="animate-spin" />
                    Building your wishful places from your interests...
                  </div>
                )}

                <span className="block text-[10px] font-bold uppercase mb-1.5" style={{ color: C.muted }}>Classic Scenes</span>
                <div className="grid grid-cols-2 gap-2">
                  {visibleBackgrounds.map((bg) => {
                    const isSelected = bg.id === background.id;
                    return (
                      <button
                        key={bg.id}
                        onClick={() => { setBackground(bg); setShowPicker(false); }}
                        className="flex items-center gap-2 p-1.5 rounded-lg text-left transition-all hover:scale-[1.02]"
                        style={{
                          background: isSelected ? 'rgba(212,168,83,0.15)' : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${isSelected ? C.gold : 'rgba(255,255,255,0.08)'}`,
                        }}
                        data-testid={`virtual-bg-option-${bg.id}`}
                      >
                        <span
                          className="block w-10 h-10 rounded-md flex-shrink-0"
                          style={{ background: bg.swatch, border: '1px solid rgba(0,0,0,0.3)' }}
                        />
                        <span className="text-xs font-semibold truncate" style={{ color: C.cream }}>{bg.name}</span>
                        {isSelected && <CheckCircle size={12} style={{ color: C.gold, marginLeft: 'auto' }} />}
                      </button>
                    );
                  })}
                </div>
                {segmenterError && (
                  <p className="text-[10px] mt-2" style={{ color: C.muted }}>
                    Background effects need a modern browser with WebAssembly. Showing raw camera only.
                  </p>
                )}
                {!segmenterError && !segmenterReady && (
                  <p className="text-[10px] mt-2 flex items-center gap-1" style={{ color: C.muted }}>
                    <Loader2 size={10} className="animate-spin" /> Loading effects...
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
});

VirtualBackgroundCamera.displayName = 'VirtualBackgroundCamera';

export default VirtualBackgroundCamera;
